import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, CaretDown, CheckCircle, Clock, Sparkle, Users, X } from "@phosphor-icons/react";
import { trackConversion } from "../../lib/analytics";
import client from "../../lib/api";
import { scrollTo } from "./utils";
import HeroWorkflowLoop from "./HeroWorkflowLoop";

export function HeroWaitlistModal({ open, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [fleetSize, setFleetSize] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [position, setPosition] = useState(null);

  // Reset transient state whenever the modal opens so re-opening after a
  // successful submit shows a fresh form (not a stale success state).
  useEffect(() => {
    if (open) {
      setEmail("");
      setFleetSize("");
      setSubmitting(false);
      setDone(false);
      setErrorMsg("");
      setPosition(null);
    }
  }, [open]);

  // ESC closes — expected UX on any focused overlay
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const clean = (email || "").trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      setErrorMsg("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const { data } = await client.post("/founding-pilots/waitlist", {
        email: clean,
        fleet_size: fleetSize || "",
      });
      trackConversion("hero_ribbon_waitlist_signup", { fleet_size: fleetSize || "" });
      setPosition(data?.position || null);
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="hero-waitlist-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hero-waitlist-title"
    >
      {/* Dimmed backdrop — clicking it closes */}
      <button
        type="button"
        aria-label="Close waitlist modal"
        onClick={onClose}
        data-testid="hero-waitlist-modal-backdrop"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      {/* Card */}
      <div className="relative w-full max-w-md rounded-2xl border border-[#FF5A1F]/25 bg-[#0A0A0A] p-6 sm:p-8 shadow-2xl shadow-[#FF5A1F]/10">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="hero-waitlist-modal-close"
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-neutral-500 hover:text-white hover:bg-white/5"
        >
          <X size={16} />
        </button>

        {!done ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold">
              <Sparkle size={10} weight="fill" /> Setup call
            </div>
            <h3 id="hero-waitlist-title" className="font-display text-xl sm:text-2xl font-semibold text-white leading-tight">
              Reserve your spot in <span className="text-[#FF5A1F]">under 10 seconds</span>.
            </h3>
            <p className="text-sm text-neutral-400 leading-relaxed">
              Drop your email — Michael will reach out to book a 15-min setup call. No spam.
            </p>
            <input
              type="email"
              required
              placeholder="you@yourfleet.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="hero-waitlist-modal-email"
              autoFocus
              className="w-full rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#FF5A1F] focus:outline-none px-4 py-3 text-white text-sm placeholder:text-neutral-600 transition-colors"
            />
            <select
              value={fleetSize}
              onChange={(e) => setFleetSize(e.target.value)}
              data-testid="hero-waitlist-modal-fleet-size"
              className="w-full rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#FF5A1F] focus:outline-none px-4 py-3 text-white text-sm transition-colors appearance-none"
            >
              <option value="">Fleet size (optional)</option>
              <option value="1">Owner-operator (1 truck)</option>
              <option value="2-4">Small fleet (2-4 trucks)</option>
              <option value="5-24">Growing fleet (5-24 trucks)</option>
              <option value="25-99">Mid-size (25-99 trucks)</option>
              <option value="100+">Large fleet (100+ trucks)</option>
            </select>
            {errorMsg && (
              <div data-testid="hero-waitlist-modal-error" className="text-xs text-red-300">
                {errorMsg}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="hero-waitlist-modal-submit"
              className="w-full btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Reserving..." : "Reserve My Spot"}
              {!submitting && <ArrowRight size={14} weight="bold" />}
            </button>
            <p className="text-[10px] text-neutral-600 uppercase tracking-widest text-center">
              7-day free trial · Cancel anytime · US-based support
            </p>
          </form>
        ) : (
          <div data-testid="hero-waitlist-modal-success" className="space-y-4 text-center py-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center">
              <CheckCircle size={28} weight="fill" className="text-[#FF5A1F]" />
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
              You're on the list.
            </h3>
            {position != null && (
              <div
                data-testid="hero-waitlist-modal-position"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/40 text-sm text-[#FF9764] font-semibold"
              >
                <Sparkle size={12} weight="fill" />
                You're #{position} on the waitlist
              </div>
            )}
            <p className="text-sm text-neutral-400 leading-relaxed">
              We'll email you within 24 hours with pilot details and a link to book a 15-min discovery call.
              Check <span className="text-white">{email}</span> for the confirmation.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="hero-waitlist-modal-done"
              className="mt-2 rounded-full px-5 py-2.5 text-xs border border-white/15 hover:border-white/40 hover:bg-white/5 text-white transition-colors"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Live scarcity ribbon — sits above the H1 and pulls real pilot-cohort data
// from /founding-pilots/count. Falls back gracefully to a static badge if the
// endpoint fails so we never render a "..." loading state above the fold.
export function HeroScarcityRibbon() {
  const [cohort, setCohort] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const refetch = () => {
    client
      .get("/founding-pilots/count")
      .then((r) => setCohort(r.data || null))
      .catch(() => setCohort(null));
  };

  // Initial fetch + 60s auto-refresh so returning visitors watch the
  // "spots left" number tick down in real time. Live-counter as social
  // proof beats a stale static badge. Cleared on unmount.
  useEffect(() => {
    let cancelled = false;
    const doFetch = () => {
      client
        .get("/founding-pilots/count")
        .then((r) => { if (!cancelled) setCohort(r.data || null); })
        .catch(() => { if (!cancelled) setCohort(null); });
    };
    doFetch();
    const id = setInterval(doFetch, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Compute the scarcity phrase. Every branch returns something usable so
  // the badge never disappears — worst case we show the static fallback.
  let scarcityText = "Practical AI for Trucking";
  let showPulse = false;
  if (cohort) {
    const target = cohort.cohort_target ?? 5;
    const remaining = cohort.cohort_remaining ?? target;
    const waitlist = cohort.waitlist_count ?? 0;
    const filled = cohort.cohort_filled === true;
    if (filled) {
      scarcityText = `Cohort 1 full · ${waitlist} on waitlist for cohort 2`;
      showPulse = true;
    } else if (waitlist > 0) {
      scarcityText = `Founding Pilot · ${remaining} of ${target} spots · ${waitlist} on waitlist`;
      showPulse = true;
    } else {
      scarcityText = `Founding Pilot Program · First ${target} fleets`;
      showPulse = true;
    }
  }

  const onRibbonClick = () => {
    trackConversion("hero_ribbon_click", { text: scarcityText });
    setModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onRibbonClick}
        data-testid="hero-scarcity-ribbon"
        className="group inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] hover:bg-[#FF5A1F]/[0.14] text-[11px] uppercase tracking-widest text-[#FF9764] mb-6 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60"
      >
        {showPulse ? (
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF5A1F] opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF5A1F]" />
          </span>
        ) : (
          <Sparkle size={12} weight="fill" />
        )}
        <span data-testid="hero-scarcity-ribbon-text">{scarcityText}</span>
        <ArrowRight size={11} weight="bold" className="text-[#FF5A1F] group-hover:translate-x-0.5 transition-transform" />
      </button>
      <HeroWaitlistModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refetch}
      />
    </>
  );
}

// Live countdown showing "Cohort 1 closes in X days" so the scarcity
// signal has a hard deadline visitors can feel. Ticks every 30 seconds
// so a visitor loitering on the page watches the "hours left" tick down.
// Hides itself if the backend doesn't provide `cohort_closes_at` or the
// deadline has already passed.
export function CohortCountdown() {
  const [closeAt, setCloseAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    client
      .get("/founding-pilots/count")
      .then((r) => { if (!cancelled) setCloseAt(r.data?.cohort_closes_at || null); })
      .catch(() => { if (!cancelled) setCloseAt(null); });
    return () => { cancelled = true; };
  }, []);

  // Ticker — 30s cadence is plenty; countdown to a future date doesn't
  // need per-second resolution and reduces re-renders.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!closeAt) return null;
  const target = new Date(closeAt).getTime();
  if (!Number.isFinite(target) || target <= now) return null;

  const diff = target - now;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

  // Copy shifts by proximity so <24h feels urgent, not stale
  const label = days >= 1
    ? `Cohort 1 closes in ${days} day${days === 1 ? "" : "s"}`
    : `Cohort 1 closes in ${hours} hour${hours === 1 ? "" : "s"}`;

  return (
    <div
      data-testid="hero-cohort-countdown"
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-400 mb-6 -mt-2"
    >
      <Clock size={11} weight="fill" className="text-[#FF9764]" />
      <span data-testid="hero-cohort-countdown-text">{label}</span>
    </div>
  );
}

export function Hero() {
  const goPreview = (e) => {
    e.preventDefault();
    trackConversion("workflow_preview_cta", { position: "hero_secondary" });
    scrollTo("workflow-preview");
  };
  const onPrimaryClick = () => {
    trackConversion("free_workflow_cta", { position: "hero_primary" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 border-b border-white/10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <Link
            to="/pricing"
            data-testid="hero-trial-ribbon"
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] hover:bg-[#FF5A1F]/[0.14] text-[11px] uppercase tracking-widest text-[#FF9764] mb-6 transition-colors"
          >
            <Sparkle size={12} weight="fill" /> 7-day free trial · $0 today · cancel anytime
          </Link>
          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-4"
          >
            The AI dispatcher that treats you like a{" "}
            <span className="text-[#FF5A1F]">partner</span>
            <br className="hidden sm:block" />
            {" "}— not a subscription.
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg leading-relaxed max-w-xl mb-4">
            I'm <span className="text-white font-medium">Michael</span> — I run FreightPilotAI from Sioux Falls, SD. In a live 30-minute Zoom, I'll set up your dispatch, invoicing, and driver payroll — then hand you the keys. You keep your data, cancel anytime, and always talk to a real US number: <span className="text-white">(605) 679-7303</span>.
          </p>

          {/* Trust ribbon — quick "what you get" chips */}
          <ul
            data-testid="hero-trust-ribbon"
            className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] sm:text-xs text-neutral-400 mb-8 max-w-xl"
          >
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-emerald-400" />
              12+ hours a week back
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-emerald-400" />
              Live in 5 business days
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-emerald-400" />
              7-day free trial, cancel anytime
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-emerald-400" />
              Export your data anytime
            </li>
          </ul>

          <MarketplaceLiveBadge />
          <WallMilestoneBadge />
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/contact?intent=concierge-setup"
              onClick={onPrimaryClick}
              data-testid="hero-primary-cta"
              className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              Talk to Michael — no bot, no signup
              <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-secondary-cta"
              className="rounded-full px-6 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              See how it works
              <CaretDown size={16} />
            </a>
          </div>
          <p className="mt-4 text-xs sm:text-sm text-neutral-400 max-w-xl leading-relaxed">
            <span className="text-[#FF9764] font-medium">$100 guarantee:</span> If FreightPilotAI hasn't saved you at least 5 hours during your 7-day trial, I'll personally write you a $100 check. My word.
          </p>
          <Link
            to="/enterprise"
            data-testid="hero-enterprise-ribbon"
            className="mt-6 inline-flex items-center gap-2 text-xs sm:text-sm text-neutral-400 hover:text-[#FF9764] transition-colors group"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
            Running 50+ trucks?
            <span className="text-neutral-500 group-hover:text-[#FF9764]">See our Enterprise plans</span>
            <ArrowRight size={12} weight="bold" className="text-[#FF5A1F] group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="relative">
          <HeroWorkflowLoop />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  MARKETPLACE LIVE BADGE  ───────────────────── */
// Live posted-load count from the public marketplace — real social proof
// that shippers use the board. Renders nothing while the count is 0 so an
// empty board never undermines trust.
function WallMilestoneBadge() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    client.get("/public/wins/stats").then(({ data }) => { if (!cancelled) setStats(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!stats || !stats.total) return null;
  const n = stats.this_month || stats.total;
  const label = stats.this_month
    ? `${n} carrier${n === 1 ? "" : "s"} booked their first load this month`
    : `${n} carrier${n === 1 ? "" : "s"} booked their first load with the free checklist`;
  return (
    <Link
      to="/authority-checklist"
      data-testid="hero-wall-milestone"
      className="mb-6 -mt-2 ml-0 sm:ml-2 inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-xs text-orange-100 hover:border-[#FF5A1F]/70 hover:bg-[#FF5A1F]/15 transition-colors"
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
      <span data-testid="hero-wall-milestone-text">{label}</span>
      <span className="text-[#FF5A1F] font-semibold">See the wall →</span>
    </Link>
  );
}

function MarketplaceLiveBadge() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/public/posted-loads/count")
      .then(({ data }) => {
        if (!cancelled) setCount(data?.active ?? 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!count) return null;
  return (
    <Link
      to="/post-load"
      data-testid="hero-marketplace-badge"
      className="mb-6 -mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs text-emerald-200 hover:border-emerald-400/60 hover:bg-emerald-500/15 transition-colors"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
      <span data-testid="hero-marketplace-badge-text">
        {count} live shipper load{count === 1 ? "" : "s"} on the board right now
      </span>
      <span className="text-emerald-300/80 font-medium">Post yours free →</span>
    </Link>
  );
}

/* ─────────────────────  FOUNDING PILOT BANNER  ───────────────────── */

export function FoundingPilotBanner() {
  const [momentum, setMomentum] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/founding-pilots/count")
      .then((r) => {
        if (cancelled) return;
        setMomentum(r.data || null);
      })
      .catch(() => setMomentum(null));
    return () => {
      cancelled = true;
    };
  }, []);

  // Compute display values with graceful fallback.
  const onboarded = momentum?.onboarded ?? 0;
  const total = momentum?.total_spots ?? 25;
  const remaining = momentum?.spots_remaining ?? total;
  const pct = Math.min(100, Math.round((onboarded / total) * 100));
  const scarcityLine =
    onboarded > 0
      ? `${onboarded} of ${total} founding-pilot spots claimed · ${remaining} left`
      : `Now accepting the first ${total} founding pilots · ${remaining} spots left`;

  return (
    <section
      id="founding-pilot"
      data-testid="founding-pilot-section"
      className="relative border-b border-white/10 py-16 sm:py-20 overflow-hidden"
    >
      {/* Subtle orange radial glow behind the card */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(60% 55% at 50% 40%, rgba(255,90,31,0.10) 0%, rgba(255,90,31,0) 70%)",
        }}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <div
          className="relative rounded-3xl border border-[#FF5A1F]/30 bg-gradient-to-br from-[#FF5A1F]/[0.08] via-black/40 to-black/20 p-8 sm:p-12"
          data-testid="founding-pilot-card"
        >
          {/* Ribbon badge */}
          <div className="absolute -top-3 left-8 sm:left-12 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF5A1F] text-black text-[10px] uppercase tracking-widest font-bold shadow-lg shadow-[#FF5A1F]/30">
            <Sparkle size={11} weight="fill" /> Founding Pilot Program
          </div>

          <div className="grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-[1.1] mb-4">
                Help shape the future of{" "}
                <span className="text-[#FF5A1F]">AI for trucking</span>.
              </h2>
              <p className="text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                We're currently working with early pilot customers to refine FreightPilot AI.
                If you're interested in helping shape the future of AI for trucking while receiving{" "}
                <span className="text-white font-medium">priority support</span> and{" "}
                <span className="text-white font-medium">preferred pricing</span>, we'd love to talk.
              </p>

              {/* Live pilot momentum counter — real data pulled from /founding-pilots/count */}
              <div
                data-testid="pilot-momentum-counter"
                className="mt-6 rounded-xl border border-[#FF5A1F]/25 bg-black/40 backdrop-blur-sm p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-2.5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF9764] font-semibold">
                    <Users size={13} weight="bold" />
                    Cohort progress
                  </div>
                  <div
                    data-testid="pilot-momentum-count"
                    className="text-xs sm:text-sm text-neutral-200 font-medium tabular-nums"
                  >
                    {scarcityLine}
                  </div>
                </div>
                <div
                  className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={total}
                  aria-valuenow={onboarded}
                  aria-label="Founding pilot spots claimed"
                >
                  <div
                    className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#FF9764] transition-[width] duration-500"
                    style={{ width: `${Math.max(pct, 4)}%` }}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/contact?intent=pilot"
                  onClick={() => trackConversion("founding_pilot_cta")}
                  data-testid="founding-pilot-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold text-sm px-6 py-3 transition-colors shadow-lg shadow-[#FF5A1F]/20"
                >
                  Claim a pilot spot <ArrowRight size={14} weight="bold" />
                </Link>
                <span className="text-[11px] text-neutral-500 uppercase tracking-widest">
                  15-min setup call · 7-day free trial
                </span>
              </div>
            </div>

            {/* Right-side benefits list */}
            <ul className="space-y-3 md:pl-6 md:border-l md:border-white/10">
              {[
                "Direct line to the founder for feature requests",
                "Preferred pricing locked in for life",
                "Priority support with same-day response",
                "First look at every new AI model + integration",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2.5 text-sm text-neutral-200 leading-snug"
                >
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="text-[#FF5A1F] mt-0.5 shrink-0"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
